import * as React from "react";
import { navTo } from "@origin-digital/event-dispatcher";
import {
  Stack,
  Heading,
  Button,
  Text,
  MarkdownLite,
  Columns,
  Column,
  TextLink,
} from "@origin-digital/ods-core";
import content from "../../cms/chat.json";
import { AsyncMessenger } from "./AsyncMessenger";
import { useAppContext } from "../../AppContext";

interface IChatProps {
  isNative: boolean;
  nativeChatSupported: boolean | undefined;
}

interface ILiveChatSectionProps {
  authenticated: boolean;
  loading: boolean;
}

// Desktop
const LiveChatSection = ({
  authenticated,
  loading,
}: ILiveChatSectionProps): JSX.Element => (
  <Stack alignX={["left", "center"]}>
    <Button
      data-id="contact-us-button"
      status={loading ? "loading" : undefined}
      onClick={() => (authenticated ? navTo.messageUs() : navTo.contactUs())}
    >
      {content.contactUs.button}
    </Button>
  </Stack>
);

// Native
// Do not destructure `content.contactUsOrEmail` properties will be undefined. This might be a wp5 bug.
// For some reason, if `content.contactUsOrEmail` used WP minification rename props of content.contactUsOrEmail.
// Assumption with repointing to Message Us page is that this is App version (isNative)
const PhoneAndEmailSection = (): JSX.Element => (
  <Columns
    alignX={["left", "center"]}
    collapseBelow="lg"
    data-id="chat-item-other"
  >
    <Column width="2/3">
      <Stack alignX={["left", "center"]}>
        <Text
          align={["left", "center"]}
          data-id={"contact"}
          data-testid="contact"
        >
          <TextLink onClick={() => navTo.messageUs()} href="#">
            {content.contactUsOrEmail.contactUs}
          </TextLink>
          <MarkdownLite>{`${content.contactUsOrEmail.email}`}</MarkdownLite>
        </Text>
      </Stack>
    </Column>
  </Columns>
);

export const Chat = (chatProps: IChatProps): JSX.Element => {
  const { isAuthenticated, categories } = useAppContext();
  const { nativeChatSupported, isNative } = chatProps;
  const { heading } = content;

  return (
    <>
      {nativeChatSupported && <AsyncMessenger />}
      <Columns
        alignX={["left", "center"]}
        collapseBelow="lg"
        space={["none", "xxxlarge"]}
        data-id="chat"
      >
        <Column width="2/3">
          <Stack alignX={["left", "center"]} space={["medium", "medium"]}>
            <Heading variant="h3">{heading}</Heading>
            <Stack space={["xlarge", "xxlarge"]}>
              {!isNative && (
                <LiveChatSection
                  authenticated={isAuthenticated}
                  loading={categories?.loading}
                />
              )}
              {isNative && <PhoneAndEmailSection />}
            </Stack>
          </Stack>
        </Column>
      </Columns>
    </>
  );
};
